import {
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    SubdirectoryArrowRight as SubcategoryIcon,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { categoriesService, subcategoriesService } from '../services/api';

// Composant pour une ligne de sous-catégorie draggable
function SortableRow({ subcategory, getCategoryName, handleEditSubcategory, handleDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subcategory.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'default',
  };

  return (
    <TableRow ref={setNodeRef} style={style} key={subcategory.id}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ 
              cursor: 'grab', 
              display: 'flex', 
              alignItems: 'center',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <DragIcon color="action" />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {subcategory.displayOrder || 0}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {subcategory.icon ? (
            <Box
              component="img"
              src={`${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${subcategory.icon}`}
              alt="Icône"
              sx={{
                width: 24,
                height: 24,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          ) : (
            <SubcategoryIcon color="secondary" />
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {subcategory.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="textSecondary">
          {subcategory.description}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={getCategoryName(subcategory.categoryId)}
          color="primary"
          size="small"
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Chip
          label={subcategory.products?.length || 0}
          color="secondary"
          size="small"
        />
      </TableCell>
      <TableCell>
        <IconButton size="small" onClick={() => handleEditSubcategory(subcategory)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => handleDelete(subcategory.id)}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

const Subcategories = () => {
  const navigate = useNavigate();
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Configuration des capteurs pour le drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subcategoriesRes, categoriesRes] = await Promise.all([
        subcategoriesService.getAll(),
        categoriesService.getAll(),
      ]);
      setSubcategories(subcategoriesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNewSubcategory = () => {
    navigate('/subcategories/new');
  };

  const handleEditSubcategory = (subcategory) => {
    navigate(`/subcategories/edit/${subcategory.id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-catégorie ?')) {
      try {
        await subcategoriesService.delete(id);
        fetchData();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'N/A';
  };

  // Gestion du drag & drop
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredSubcategories.findIndex((sub) => sub.id === active.id);
      const newIndex = filteredSubcategories.findIndex((sub) => sub.id === over.id);

      // Réorganiser localement
      const newOrder = arrayMove(filteredSubcategories, oldIndex, newIndex);
      
      // Mettre à jour les displayOrder
      const updatedSubcategories = newOrder.map((sub, index) => ({
        ...sub,
        displayOrder: index,
      }));
      
      // Mettre à jour l'état local immédiatement pour un retour visuel instantané
      setSubcategories((prevSubcategories) => {
        const updatedAll = [...prevSubcategories];
        updatedSubcategories.forEach((updated) => {
          const idx = updatedAll.findIndex((s) => s.id === updated.id);
          if (idx !== -1) {
            updatedAll[idx] = updated;
          }
        });
        return updatedAll.sort((a, b) => a.displayOrder - b.displayOrder);
      });

      // Envoyer la mise à jour au serveur
      try {
        await subcategoriesService.updateOrder(
          updatedSubcategories.map((sub) => ({ id: sub.id, displayOrder: sub.displayOrder }))
        );
      } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'ordre:', error);
        // En cas d'erreur, recharger les données
        fetchData();
      }
    }
  };

  const filteredSubcategories = subcategories.filter(subcategory =>
    subcategory.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subcategory.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getCategoryName(subcategory.categoryId).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedSubcategories = filteredSubcategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return <LoadingSpinner message="Chargement des sous-catégories..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Gestion des Sous-catégories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewSubcategory}
        >
          Nouvelle Sous-catégorie
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher une sous-catégorie..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ordre</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Catégorie parente</TableCell>
              <TableCell>Produits</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={paginatedSubcategories.map((sub) => sub.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {paginatedSubcategories.map((subcategory) => (
                  <SortableRow
                    key={subcategory.id}
                    subcategory={subcategory}
                    getCategoryName={getCategoryName}
                    handleEditSubcategory={handleEditSubcategory}
                    handleDelete={handleDelete}
                  />
                ))}
              </TableBody>
            </SortableContext>
          </DndContext>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredSubcategories.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default Subcategories;
